import { useEffect } from 'react';
import { FormikProps, useFormik } from 'formik';
import * as Yup from 'yup';

import { useAppSelector } from 'store/store.hook';
import { selectCurrentUserInfo } from 'store/slices/auth.slice';
import { useUpdateOrgBilingAddressMutation } from 'store/apis/org.api';

/**
 * Todo:
 * Do not use any type.
 * Implement new deep object comparison
 */
const compareAddresses = (addressInfo1: any, addressInfo2: any) => {
  if (!addressInfo1 || !addressInfo2) {
    return false;
  }

  const { address_1: address1 = '', city: city1 = '', state: state1 = '', zip: zip1 = '' } = addressInfo1 || {};
  const { address_1: address2 = '', city: city2 = '', state: state2 = '', zip: zip2 = '' } = addressInfo2 || {};

  return (
    address1 &&
    address2 &&
    city1 &&
    city2 &&
    state1 &&
    state2 &&
    zip1 &&
    zip2 &&
    address1 === address2 &&
    city1 === city2 &&
    state1 === state2 &&
    zip1 === zip2
  );
};

interface UseBillingAddressFormManagerProps {
  next?: (data?: any) => void;
}

export const useBillingAddressFormManager = ({ next = () => {} }: UseBillingAddressFormManagerProps) => {
  const { orgSlug, billingAddress: billing, shippingAddress: shipping } = useAppSelector(selectCurrentUserInfo);
  const [updateBillingAddress, { isLoading, isSuccess, isError, ...rest }] = useUpdateOrgBilingAddressMutation();

  const initialValues = {
    billing_address1: billing?.address_1 || '',
    billing_address2: billing?.address_2 || '',
    billing_city: billing?.city || '',
    billing_state: billing?.state || '',
    billing_zip: billing?.zip || '',
    use_billing: compareAddresses(billing, shipping),
  };

  const validationSchema = Yup.object({
    billing_address1: Yup.string().nullable().required('Billing address is required.'),
    billing_city: Yup.string().nullable().required('Billing city is required.'),
    billing_state: Yup.string().nullable().required('Billing state is required.'),
    billing_zip: Yup.string()
      .nullable()
      .required('Billing zip code is required.')
      .matches(/\b\d{5}\b/, { message: 'Zip code must be 5 digits.', excludeEmptyString: true }),
    use_billing: Yup.boolean(),
  });

  const formManager = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      const { billing_address1, billing_address2, billing_city, billing_state, billing_zip, use_billing } = values;

      const billing_address = {
        id: null,
        address_1: billing_address1,
        address_2: billing_address2,
        city: billing_city,
        state: billing_state,
        zip: billing_zip,
      };

      const shipping_address = use_billing ? billing_address : shipping || null;

      updateBillingAddress({
        orgSlug,
        billing_address,
        shipping_address,
      });
    },
  });

  useEffect(() => {
    if (!isLoading && isSuccess && !isError) {
      const { data } = rest;

      next(data);
    }
  }, [isLoading, isSuccess, isError]);

  return [
    formManager,
    {
      isLoading,
      isSuccess,
      isError,
    },
  ] as [
    FormikProps<typeof initialValues>,
    {
      isLoading: boolean;
      isSuccess: boolean;
      isError: boolean;
    },
  ];
};
